import { useState } from 'react';
import { CatalogExam } from '../../types/catalog.response';
import SearchableDropdown from '../../components/shared/SearchableDropdown';

interface SelectedExamRow {
    examId: string;
    examType: string;
    scoringType: string;
    score: string;
    scoreAlpha: string;
}

export interface ExamEntry {
    examId: string;
    scoringType: string;
    score: number | null;
    scoreAlpha: string | null;
}

interface ExamSelectionFormProps {
    exams: CatalogExam[];
    onComplete: (exams: ExamEntry[]) => void;
    onBack: () => void;
    loading?: boolean;
}

export default function ExamSelectionForm({
    exams,
    onComplete,
    onBack,
    loading = false
}: ExamSelectionFormProps) {
    const [selectedExams, setSelectedExams] = useState<SelectedExamRow[]>([]);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const examTypes = Array.from(new Set(exams.map(e => e.type))).sort();

    const addExamRow = () => {
        setSelectedExams(prev => [
            ...prev,
            { examId: '', examType: '', scoringType: '', score: '', scoreAlpha: '' }
        ]);
        setSubmitError(null);
    };

    const removeExamRow = (index: number) => {
        setSelectedExams(prev => prev.filter((_, i) => i !== index));
    };

    const handleTypeChange = (index: number, type: string) => {
        setSelectedExams(prev => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                examType: type,
                examId: '',
                scoringType: '',
                score: '',
                scoreAlpha: ''
            };
            return next;
        });
        setSubmitError(null);
    };

    const handleExamChange = (index: number, examId: string) => {
        const exam = exams.find(e => e.id.toString() === examId);
        if (!exam) return;

        setSelectedExams(prev => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                examId,
                scoringType: exam.scoringType,
                score: '',
                scoreAlpha: ''
            };
            return next;
        });
        setSubmitError(null);
    };

    const handleScoreChange = (index: number, value: string) => {
        setSelectedExams(prev => {
            const next = [...prev];
            next[index] = { ...next[index], score: value };
            return next;
        });
        setSubmitError(null);
    };

    const handleScoreAlphaChange = (index: number, value: string) => {
        setSelectedExams(prev => {
            const next = [...prev];
            next[index] = { ...next[index], scoreAlpha: value };
            return next;
        });
        setSubmitError(null);
    };

    const validateForm = () => {
        for (let i = 0; i < selectedExams.length; i++) {
            const row = selectedExams[i];
            if (!row.examType) return 'Please select an exam type for all rows';
            if (!row.examId) return 'Please select an exam name for all rows';
            if (row.scoringType === 'NUMERIC' && !row.score) {
                return 'Please enter a score for all numeric exams';
            }
            if (row.scoringType === 'ALPHA' && !row.scoreAlpha) {
                return 'Please enter a score for all alpha exams';
            }
        }
        return null;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const error = validateForm();
        if (error) {
            setSubmitError(error);
            return;
        }

        const entries: ExamEntry[] = selectedExams.map(row => ({
            examId: row.examId,
            scoringType: row.scoringType,
            score:
                row.scoringType === 'NUMERIC'
                    ? parseFloat(row.score)
                    : row.scoringType === 'PASS_FAIL'
                    ? 1
                    : null,
            scoreAlpha: row.scoringType === 'ALPHA' ? row.scoreAlpha : null,
        }));

        onComplete(entries);
    };

    const hasExams = selectedExams.length > 0;

    return (
        <form onSubmit={handleSubmit} className="space-y-8 mb-16">

            <div className="space-y-4">
                {!hasExams && (
                    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5">
                        <p className="text-sm font-medium text-gray-900 mb-1">
                            No exams added yet
                        </p>
                        <p className="text-sm text-gray-500 mb-4">
                            Add your first exam to get more accurate requirement matching.
                        </p>
                        <button
                            type="button"
                            onClick={addExamRow}
                            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            Add your first exam
                        </button>
                    </div>
                )}

                {hasExams && (
                    <div className="space-y-4">
                        {selectedExams.map((row, index) => {
                            const examsForType = exams.filter(e => e.type === row.examType);
                            const otherSelectedIds = selectedExams
                                .filter((_, i) => i !== index)
                                .map(r => r.examId)
                                .filter(Boolean);

                            return (
                                <div
                                    key={index}
                                    className="rounded-xl border border-gray-200 bg-white p-4"
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1">
                                            <SearchableDropdown
                                                value={row.examType}
                                                options={examTypes.map(t => ({ _id: t, name: t }))}
                                                onChange={(type) => handleTypeChange(index, type)}
                                                excludeIds={[]}
                                                placeholder="Select type..."
                                                noResultsText="No types found"
                                            />
                                        </div>

                                        <div className="flex-1">
                                            <SearchableDropdown
                                                value={row.examId}
                                                options={examsForType.map(e => ({
                                                    _id: e.id.toString(),
                                                    name: e.name
                                                }))}
                                                onChange={(examId) => handleExamChange(index, examId)}
                                                excludeIds={otherSelectedIds}
                                                placeholder={
                                                    row.examType
                                                        ? 'Select exam...'
                                                        : 'Select type first...'
                                                }
                                                noResultsText="No exams found"
                                            />
                                        </div>

                                        {row.scoringType === 'NUMERIC' && (
                                            <input
                                                type="number"
                                                step="any"
                                                value={row.score}
                                                onChange={(e) =>
                                                    handleScoreChange(index, e.target.value)
                                                }
                                                placeholder="Score"
                                                className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        )}

                                        {row.scoringType === 'ALPHA' && (
                                            <input
                                                type="text"
                                                value={row.scoreAlpha}
                                                onChange={(e) =>
                                                    handleScoreAlphaChange(index, e.target.value)
                                                }
                                                placeholder="Score"
                                                className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        )}

                                        <button
                                            type="button"
                                            onClick={() => removeExamRow(index)}
                                            className="text-gray-400 hover:text-gray-600"
                                            aria-label="Remove exam"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        <button
                            type="button"
                            onClick={addExamRow}
                            className="text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center gap-1 mt-1"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            Add another exam
                        </button>
                    </div>
                )}
            </div>

            {submitError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                    <p className="text-sm">{submitError}</p>
                </div>
            )}

            <div className="flex items-center justify-between pt-2">
                <button
                    type="button"
                    onClick={onBack}
                    className="rounded-md border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                    Back
                </button>

                <button
                    type="submit"
                    disabled={loading}
                    className="rounded-md border border-transparent bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                    {loading ? 'Saving...' : 'Finish setup'}
                </button>
            </div>
        </form>
    );
}